import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ToastProvider } from './components/ui/TWToast';

// Context imports
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Component imports (always loaded)
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuthenticatedLayout } from './components/layout/AuthenticatedLayout';
import { FeatureGate } from './components/shared/FeatureGate';
import { CookieBannerTW } from './components/shared/cookie/CookieBannerTW';
import { TWThemeToggle } from './components/ui/TWThemeToggle';
import { TWLoginIcon } from './components/ui/TWLoginIcon';
import { TWLogoIcon } from './components/ui/TWLogoIcon';
import { TWFeedbackButton } from './components/ui/TWFeedbackButton';

// Essential pages (loaded immediately)
import { HomePage } from './pages/HomePage';
import { LoginPageTW } from './pages/auth/LoginPageTW';
// RegisterPageTW + RegisterFormTW wurden 2026-05-21 entfernt — die Kombi-
// Checkout-Wizard-Architektur legt Accounts ausschließlich beim Kauf an
// (siehe CheckoutAccountForm.tsx). Die /register-Route bleibt unten als
// Navigate-Redirect zu /pakete, damit alte Bookmarks und Mail-Links nicht
// 404 werfen.
import { ForgotPasswordPageTW } from './pages/auth/ForgotPasswordPageTW';
import { ResetPasswordPageTW } from './pages/auth/ResetPasswordPageTW';
import { EmailVerificationPageTW } from './pages/auth/EmailVerificationPageTW';
import { SubscriptionRequiredPageTW } from './pages/auth/SubscriptionRequiredPageTW';
import { DashboardPageTW } from './pages/DashboardPageTW';
import { ProfilePage } from './pages/ProfilePage';
// Checkout lazy laden, damit Stripe.js (146 KiB) nicht auf jeder Seite geladen wird
const CheckoutPage = lazy(() => import('./pages/CheckoutPageTW').then(m => ({ default: m.CheckoutPageTW })));
const CheckoutReturnPage = lazy(() => import('./pages/CheckoutReturnPageTW').then(m => ({ default: m.CheckoutReturnPageTW })));

// Lazy loaded pages (loaded on demand)
const AdminDashboard = lazy(() => import('./pages/AdminDashboardTW').then(m => ({ default: m.AdminDashboardTW })));
const AdminFeedbackManagement = lazy(() => import('./pages/AdminFeedbackManagementTW').then(m => ({ default: m.AdminFeedbackManagementTW })));
const SystemPageTW = lazy(() => import('./pages/admin/SystemPageTW'));
const AdminAppointmentsPage = lazy(() => import('./pages/admin/AdminAppointmentsPageTW'));
const AdminBlogPremiumSupportPage = lazy(() => import('./pages/admin/blog/AdminBlogPremiumSupportPageTW'));
const EmailPreviewPage = lazy(() => import('./pages/admin/EmailPreviewPageTW'));
const EmailQueuePage = lazy(() => import('./pages/admin/EmailQueuePageTW'));
const PricingOverviewPage = lazy(() => import('./pages/admin/PricingOverviewPageTW'));
const PricingModulesPage = lazy(() => import('./pages/admin/PricingModulesPageTW'));
const PricingModuleEditPage = lazy(() => import('./pages/admin/PricingModuleEditPageTW'));
const CouponManagementPage = lazy(() => import('./pages/admin/CouponManagementPageTW'));
const BetaManagementPage = lazy(() => import('./pages/admin/BetaManagementPageTW'));
const AdminRevocationsPage = lazy(() => import('./pages/admin/AdminRevocationsPageTW'));
const LegalPage = lazy(() => import('./pages/legal/LegalPageTW').then(m => ({ default: m.LegalPageTW })));
const TermsPage = lazy(() => import('./pages/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const CancellationPage = lazy(() => import('./pages/legal/CancellationPage').then(m => ({ default: m.CancellationPage })));
const CancellationConfirmPage = lazy(() => import('./pages/legal/CancellationConfirmPage').then(m => ({ default: m.CancellationConfirmPage })));
const WithdrawalPage = lazy(() => import('./pages/legal/WithdrawalPage').then(m => ({ default: m.WithdrawalPage })));
const WithdrawalConfirmPage = lazy(() => import('./pages/legal/WithdrawalConfirmPage').then(m => ({ default: m.WithdrawalConfirmPage })));
const FeedbackPageTW = lazy(() => import('./pages/feedback/FeedbackPageTW').then(m => ({ default: m.FeedbackPageTW })));
const AppointmentsPageTW = lazy(() => import('./pages/AppointmentsPageTW').then(m => ({ default: m.AppointmentsPageTW })));
const BlogOverviewPage = lazy(() => import('./pages/blog/BlogOverviewPageTW'));
const BlogPostDetailPageTW = lazy(() => import('./pages/blog/BlogPostDetailPageTW'));
const BlogUnsubscribedPageTW = lazy(() => import('./pages/blog/BlogUnsubscribedPageTW').then(m => ({ default: m.BlogUnsubscribedPageTW })));
const AdminBlogPostsPage = lazy(() => import('./pages/admin/AdminBlogPostsPageTW'));
const AdminBlogPostNewPage = lazy(() => import('./pages/admin/AdminBlogPostNewPageTW'));
const AdminBlogPostEditPage = lazy(() => import('./pages/admin/AdminBlogPostEditPageTW'));
const AdminBlogPostPreviewPage = lazy(() => import('./pages/admin/AdminBlogPostPreviewPage'));
const AdminMediaLibraryPageTW = lazy(() => import('./pages/admin/AdminMediaLibraryPageTW'));
const AssetManagementPageTW = lazy(() => import('./pages/admin/AssetManagementPageTW'));
const AdminYoutubeMatchingPage = lazy(() => import('./pages/admin/fcms/AdminYoutubeMatchingPageTW'));
const AdminFcmsDashboardPage = lazy(() => import('./pages/admin/fcms/AdminFcmsDashboardPageTW'));
const AdminAnnouncementsPage = lazy(() => import('./pages/admin/AdminAnnouncementsPageTW'));
const AdminAffiliatesPage = lazy(() => import('./pages/admin/AdminAffiliatesPageTW'));
const AdminRssFeedsPage = lazy(() => import('./pages/admin/AdminRssFeedsPageTW'));
const AdminRevenuePage = lazy(() => import('./pages/admin/AdminRevenuePageTW'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/users/AdminUserDetailPageTW'));
const AdminStyleguidePage = lazy(() => import('./pages/admin/AdminStyleguidePageTW'));

const ContactPageTW = lazy(() => import('./pages/ContactPageTW'));

// FCMS Pages - lazy loaded (Financial Content Management System)
const FcmsVorbereitungPage = lazy(() => import('./pages/fcms/FcmsVorbereitungPage'));
const FcmsPlanungPage = lazy(() => import('./pages/fcms/FcmsPlanungPage'));

// Academy Pages - lazy loaded (Investment-Academy: Investment-Simulator mit Elliott-Wellen-Analyse + Lern-Module)
const AcademyHomePage = lazy(() => import('./pages/academy/AcademyHomePage'));
const PlanTradeGatePage = lazy(() => import('./pages/academy/PlanTradeGatePage'));
const PlanTradeWelcomePage = lazy(() => import('./pages/academy/PlanTradeWelcomePage'));
const PlanTradeExpiredPage = lazy(() => import('./pages/academy/PlanTradeExpiredPage'));
const PlanTradeOnboardingPage = lazy(() => import('./pages/academy/PlanTradeOnboardingPage'));
const PlanTradeAssetDetailPage = lazy(() => import('./pages/academy/PlanTradeAssetDetailPage'));
const PatternPickerDemoPage = lazy(() => import('./pages/academy/PatternPickerDemoPage'));

// Elliott Wave Explorer - lazy loaded
const ElliottWavePageTW = lazy(() => import('./pages/ElliottWavePageTW'));

// HomePage V2 - Preview-Variante der Landingpage (Route /v2)
const HomePageV2 = lazy(() => import('./pages/HomePageV2').then(m => ({ default: m.HomePageV2 })));

// GmbH Pages - lazy loaded
const GmbHPortfolioPage = lazy(() => import('./pages/gmbh/GmbHPortfolioPageTW'));
const GmbHOrdersPage = lazy(() => import('./pages/gmbh/GmbHOrdersPageTW'));
const GmbHBuchhaltungPage = lazy(() => import('./pages/gmbh/GmbHBuchhaltungPageTW'));
const GmbHSettingsPage = lazy(() => import('./pages/gmbh/GmbHSettingsPageTW'));
const GmbHDashboardPage = lazy(() => import('./pages/gmbh/GmbHDashboardPageTW'));

// Eigenbau-Buchhaltung (Phase 1.3 — Frontend Core)
const AccountingAccountsPage = lazy(() => import('./pages/accounting/AccountingAccountsPageTW'));
const AccountingBookingsPage = lazy(() => import('./pages/accounting/AccountingBookingsPageTW'));
const AccountingDocumentsPage = lazy(() => import('./pages/accounting/AccountingDocumentsPageTW'));
const AccountingBookingNewPage = lazy(() => import('./pages/accounting/AccountingBookingNewPageTW'));
const AccountingPeriodsPage = lazy(() => import('./pages/accounting/AccountingPeriodsPageTW'));
const AccountingTaxNoticesPage = lazy(() => import('./pages/accounting/AccountingTaxNoticesPageTW'));
const AccountingElsterPage = lazy(() => import('./pages/accounting/AccountingElsterPageTW'));
const AccountingTemplatesPage = lazy(() => import('./pages/accounting/AccountingTemplatesPageTW'));
const AccountingReportsPage = lazy(() => import('./pages/accounting/AccountingReportsPageTW'));
const AccountingProcessDocsPage = lazy(() => import('./pages/accounting/AccountingProcessDocsPageTW'));
const AccountingAuditLogPage = lazy(() => import('./pages/accounting/AccountingAuditLogPageTW'));
const AccountingPlausibilityDismissalsPage = lazy(() => import('./pages/accounting/AccountingPlausibilityDismissalsPageTW'));
const AccountingExportPage = lazy(() => import('./pages/accounting/AccountingExportPageTW'));
const AccountingCompanyInfoPage = lazy(() => import('./pages/accounting/AccountingCompanyInfoPageTW'));
const AccountingRecipientsPage = lazy(() => import('./pages/accounting/AccountingRecipientsPageTW'));
const AccountingInvoicesPage = lazy(() => import('./pages/accounting/AccountingInvoicesPageTW'));
const AccountingInvoiceEditorPage = lazy(() => import('./pages/accounting/AccountingInvoiceEditorPageTW'));
const AccountingInboundEInvoicesPage = lazy(() => import('./pages/accounting/AccountingInboundEInvoicesPageTW'));
const AccountingTestTenantPage = lazy(() => import('./pages/accounting/AccountingTestTenantPageTW'));
const AccountingMigrationPage = lazy(() => import('./pages/accounting/AccountingMigrationPageTW'));
const AccountingWiseSettingsPage = lazy(() => import('./pages/accounting/AccountingWiseSettingsPageTW'));
const ConnectionsKrakenPage = lazy(() => import('./pages/connections/ConnectionsKrakenPageTW'));
const ConnectionsStripePage = lazy(() => import('./pages/connections/ConnectionsStripePage'));
const ConnectionsIbkrPage = lazy(() => import('./pages/connections/ConnectionsIbkrPageTW'));
const ConnectionsBitgetPage = lazy(() => import('./pages/connections/ConnectionsBitgetPageTW'));
const ConnectionsVividPage = lazy(() => import('./pages/connections/ConnectionsVividPage'));
const AccountingCashPositionPage = lazy(() => import('./pages/accounting/AccountingCashPositionPageTW'));
const AccountingProposalsPage = lazy(() => import('./pages/accounting/AccountingProposalsPageTW'));
const AccountingForecastPage = lazy(() => import('./pages/accounting/AccountingForecastPageTW'));
const AccountingProvisionsPage = lazy(() => import('./pages/accounting/AccountingProvisionsPageTW'));
const AccountingWiseInsightsPage = lazy(() => import('./pages/accounting/AccountingWiseInsightsPageTW'));

// Fullscreen-Loader für öffentliche Seiten
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-950">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
      <p className="text-gray-400">Wird geladen...</p>
    </div>
  </div>
);

// Content-Loader für authentifizierte Seiten (Sidebar bleibt sichtbar)
const ContentLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-950">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-3"></div>
      <p className="text-gray-400 text-sm">Wird geladen...</p>
    </div>
  </div>
);

// Wrapper for lazy-loaded authenticated routes
const LazyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<ContentLoader />}>
    {children}
  </Suspense>
);

// Layout-Wrapper für Blog: Eingeloggt = mit Sidebar, Nicht eingeloggt (Google) = ohne
const OptionalAuthLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const splashDone = useRef(false);

  // Splash als "managed" markieren, damit App.tsx es nicht vorzeitig entfernt
  useEffect(() => {
    const splash = document.getElementById('splash-overlay');
    if (splash) splash.dataset.managed = 'true';
  }, []);

  // Splash entfernen, sobald Auth-Check abgeschlossen
  useEffect(() => {
    if (!isLoading && !splashDone.current) {
      splashDone.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const splash = document.getElementById('splash-overlay');
          if (splash) {
            splash.style.opacity = '0';
            splash.style.transition = 'opacity 0.15s ease-out';
            setTimeout(() => splash.remove(), 150);
          }
        });
      });
    }
  }, [isLoading]);

  // Während Auth-Check: nichts rendern (Splash-Overlay verdeckt alles)
  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <AuthenticatedLayout />;
  }

  // Nicht eingeloggt: Seite ohne Sidebar rendern (für Google SEO)
  return <Outlet />;
};

function App() {
  // Redirect from 127.0.0.1 to localhost for WebAuthn compatibility
  useEffect(() => {
    if (window.location.hostname === '127.0.0.1') {
      const newUrl = window.location.href.replace('127.0.0.1', 'localhost');
      window.location.replace(newUrl);
    }
  }, []);

  // Splash-Overlay Fallback: Für öffentliche Routen (ProtectedRoute verwaltet geschützte)
  useEffect(() => {
    const timer = setTimeout(() => {
      const splash = document.getElementById('splash-overlay');
      if (splash && !splash.dataset.managed) {
        splash.style.opacity = '0';
        splash.style.transition = 'opacity 0.15s ease-out';
        setTimeout(() => splash.remove(), 150);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    // Future-Flags: schalten zwei React-Router-v7-Verhalten schon in v6 ein
    // (Transitions-Wrapping + relative Splat-Pfade). Entfernt die beiden
    // Konsolen-Warnungen und macht ein späteres v7-Upgrade reibungslos.
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/*
        Default-Meta-Tags für alle Seiten (Site-weite Defaults).
        Pro-Seite-Helmets (HomePage, BlogPostDetailPageTW, ...) überschreiben
        diese Werte sauber via react-helmet-async (data-rh="true"), so dass
        keine Duplikate (statisch + helmet) im DOM landen.
      */}
      <Helmet>
        <title>RENDITEFUCHS | Bitcoin-Analysen, Elliott-Wellen & Beratung</title>
        <meta name="description" content="Bitcoin-Analysen und individuelle Bitcoin-Beratung aus Reutlingen/Tübingen. Elliott-Wellen-Prognosen und fundiertes Bitcoin-Wissen, zugeschnitten auf deine Situation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://renditefuchs.de/" />
        <meta property="og:title" content="RENDITEFUCHS | Bitcoin-Analysen, Elliott-Wellen & Beratung" />
        <meta property="og:description" content="Bitcoin-Analysen und individuelle Bitcoin-Beratung aus Reutlingen/Tübingen. Elliott-Wellen-Prognosen und fundiertes Bitcoin-Wissen, zugeschnitten auf deine Situation." />
        <meta property="og:image" content="https://renditefuchs.de/favicon-512x512.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="RENDITEFUCHS | Bitcoin-Analysen, Elliott-Wellen & Beratung" />
        <meta name="twitter:description" content="Bitcoin-Analysen und individuelle Bitcoin-Beratung aus Reutlingen/Tübingen. Elliott-Wellen-Prognosen und fundiertes Bitcoin-Wissen, zugeschnitten auf deine Situation." />
        <meta name="twitter:image" content="https://renditefuchs.de/favicon-512x512.png" />
      </Helmet>
      <ToastProvider>
        <AuthProvider>
          <NotificationProvider>
            <div className="App">
              <Routes>
              {/* Public Routes - ohne Sidebar */}
              <Route path="/" element={<HomePage />} />
              <Route path="/v2" element={<LazyRoute><HomePageV2 /></LazyRoute>} />
              <Route path="/login" element={<LoginPageTW />} />
              {/* /register existiert nicht mehr als eigenständige Seite —
                  Kombi-Checkout führt jetzt durch Registrierung + Verifikation +
                  Zahlung als einen Flow. Bestehende Bookmarks landen auf /pakete. */}
              <Route path="/register" element={<Navigate to="/pakete" replace />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPageTW />} />
              <Route path="/reset-password" element={<ResetPasswordPageTW />} />
              <Route path="/verify-email" element={<EmailVerificationPageTW />} />

              {/* ============================================== */}
              {/* Pricing / Paket-Auswahl — öffentlich            */}
              {/* Kanonische URL: /pakete                         */}
              {/* /abo-erforderlich bleibt als Alias für Bestands- */}
              {/* Links aus Mails und Bookmarks erhalten.         */}
              {/* ============================================== */}
              <Route path="/pakete" element={<SubscriptionRequiredPageTW />} />
              <Route path="/abo-erforderlich" element={<Navigate to="/pakete" replace />} />

              {/* Kombi-Checkout — öffentlich (Schritt A: Stammdaten + Verify) */}
              <Route path="/checkout" element={<LazyRoute><CheckoutPage /></LazyRoute>} />
              <Route path="/checkout/return" element={<LazyRoute><CheckoutReturnPage /></LazyRoute>} />

              {/* ============================================== */}
              {/* Blog Routes - öffentlich für SEO                */}
              {/* Eingeloggt: mit Sidebar (OptionalAuthLayout)   */}
              {/* Nicht eingeloggt (Google): ohne Sidebar         */}
              {/* ============================================== */}
              <Route element={<OptionalAuthLayout />}>
                <Route path="/blog" element={<Suspense fallback={<PageLoader />}><FeatureGate featureKey="blog_module"><BlogOverviewPage /></FeatureGate></Suspense>} />
                {/* Unsubscribe-Bestätigung MUSS vor /blog/:slug stehen, sonst matched die Detail-Route */}
                <Route path="/blog/unsubscribed" element={<Suspense fallback={<PageLoader />}><BlogUnsubscribedPageTW /></Suspense>} />
                <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><FeatureGate featureKey="blog_module"><BlogPostDetailPageTW /></FeatureGate></Suspense>} />

                {/* Elliott Wave Explorer - öffentlich zugänglich */}
                <Route path="/elliott-wave" element={<LazyRoute><ElliottWavePageTW /></LazyRoute>} />

              </Route>

              {/* ============================================== */}
              {/* Protected Routes OHNE Pflicht-Abo               */}
              {/* (Profil, Checkout, Pflicht-Auswahl)            */}
              {/* Erreichbar für Nutzer ohne aktives Abo, damit  */}
              {/* sie es abschliessen oder den Account verwalten */}
              {/* können.                                         */}
              {/* ============================================== */}
              <Route element={<ProtectedRoute requireSubscription={false}><AuthenticatedLayout /></ProtectedRoute>}>
                {/* Profil - nicht lazy */}
                <Route path="/profil/:tab?" element={<ProfilePage />} />
              </Route>

              {/* ============================================== */}
              {/* Protected Routes mit persistenter Sidebar      */}
              {/* Die Sidebar wird nur EINMAL gerendert und      */}
              {/* bleibt beim Seitenwechsel bestehen.            */}
              {/* Pflicht-Abo erforderlich (Default).            */}
              {/* ============================================== */}
              <Route element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
                {/* Dashboard - nicht lazy, wird sofort geladen */}
                <Route path="/dashboard" element={<DashboardPageTW />} />

                {/* Feedback - lazy loaded (Pflicht-Abo erforderlich) */}
                <Route path="/feedback" element={<LazyRoute><FeedbackPageTW /></LazyRoute>} />
                {/* Legacy routes redirect to new feedback page */}
                <Route path="/community" element={<LazyRoute><FeedbackPageTW /></LazyRoute>} />
                <Route path="/my-feedback" element={<LazyRoute><FeedbackPageTW /></LazyRoute>} />

                {/* Appointments - lazy loaded, Beta-geschützt */}
                <Route path="/termine" element={<LazyRoute><FeatureGate featureKey="termine_module"><AppointmentsPageTW /></FeatureGate></LazyRoute>} />

                {/* GmbH Routes - Beta-geschützt (ein Feature für den ganzen Bereich) */}
                <Route path="/gmbh/portfolio" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><GmbHPortfolioPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/orders" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><GmbHOrdersPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/buchhaltung" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><GmbHBuchhaltungPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/settings" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><GmbHSettingsPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/dashboard" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><GmbHDashboardPage /></FeatureGate></LazyRoute>} />

                {/* Eigenbau-Buchhaltung Routes — Phase 1.3 */}
                <Route path="/accounting/accounts" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingAccountsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/bookings" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingBookingsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/documents" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingDocumentsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/booking/new" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingBookingNewPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/periods" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingPeriodsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/tax-notices" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingTaxNoticesPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/elster" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingElsterPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/templates" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingTemplatesPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/reports" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingReportsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/process-docs" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingProcessDocsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/audit-log" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingAuditLogPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/plausibility-dismissals" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingPlausibilityDismissalsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/export" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingExportPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/company-info" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingCompanyInfoPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/recipients" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingRecipientsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/invoices" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingInvoicesPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/invoices/new" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingInvoiceEditorPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/invoices/:id" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingInvoiceEditorPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/inbound-einvoices" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingInboundEInvoicesPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/test-tenant" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingTestTenantPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/migration" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingMigrationPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/wise" element={<Navigate to="/gmbh/connections/wise" replace />} />
                <Route path="/gmbh/connections" element={<Navigate to="/gmbh/connections/wise" replace />} />
                <Route path="/gmbh/connections/wise" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingWiseSettingsPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/connections/stripe" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><ConnectionsStripePage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/connections/ibkr" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><ConnectionsIbkrPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/connections/kraken" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><ConnectionsKrakenPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/connections/bitget" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><ConnectionsBitgetPage /></FeatureGate></LazyRoute>} />
                <Route path="/gmbh/connections/vivid" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><ConnectionsVividPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/cash-position" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingCashPositionPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/proposals" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingProposalsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/forecast" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingForecastPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/provisions" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingProvisionsPage /></FeatureGate></LazyRoute>} />
                <Route path="/accounting/wise-insights" element={<LazyRoute><FeatureGate featureKey="gmbh_module"><AccountingWiseInsightsPage /></FeatureGate></LazyRoute>} />

                {/* FCMS Routes (Financial Content Management)     */}
                {/* Steckbrief, Vorbereitung, Planung, etc.        */}
                {/* ============================================== */}
                <Route path="/fcms" element={<LazyRoute><FeatureGate featureKey="fcms_module"><FcmsVorbereitungPage /></FeatureGate></LazyRoute>} />
                <Route path="/fcms/planung" element={<LazyRoute><FeatureGate featureKey="fcms_module"><FcmsPlanungPage /></FeatureGate></LazyRoute>} />

                {/* Academy-Routen liegen bewusst NICHT mehr hier (Abo-Block),
                    sondern in einem eigenen requireSubscription={false}-Block
                    weiter unten - Zugang über Feature-Flags + access_grants,
                    kein Plattform-Abo nötig. */}
                <Route
                  path="/settings"
                  element={
                    <div className="flex items-center justify-center min-h-[50vh]">
                      <div className="text-center">
                        <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Einstellungen</h1>
                        <p className="mb-4 text-gray-500 dark:text-gray-400">Coming soon</p>
                        <a href="/dashboard" className="text-brand hover:text-brand-hover transition-colors">Zurück zum Dashboard</a>
                      </div>
                    </div>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <div className="flex items-center justify-center min-h-[50vh]">
                      <div className="text-center">
                        <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Abonnements</h1>
                        <p className="mb-4 text-gray-500 dark:text-gray-400">Coming soon</p>
                        <a href="/dashboard" className="text-brand hover:text-brand-hover transition-colors">Zurück zum Dashboard</a>
                      </div>
                    </div>
                  }
                />
              </Route>

              {/* ============================================== */}
              {/* Academy: KEIN Plattform-Abo (requireSubscription={false}).     */}
              {/* Sichtbarkeit/Reife steuert das Feature-Flag-System             */}
              {/* (academy / academy_plan_trade). Die Bezahlung läuft getrennt   */}
              {/* über academy.access_grants (Trial/Lifetime), geprüft im        */}
              {/* PlanTradeGate -> "nur Einmalkauf, kein Abo".                   */}
              {/* ============================================== */}
              <Route element={<ProtectedRoute requireSubscription={false}><AuthenticatedLayout /></ProtectedRoute>}>
                {/* Modul-Übersicht: am Parent-Feature (sichtbar, sobald ein Modul reif ist) */}
                <Route path="/academy" element={<LazyRoute><FeatureGate featureKey="academy"><AcademyHomePage /></FeatureGate></LazyRoute>} />

                {/* Plan-the-Trade-Modul (academy_plan_trade) */}
                <Route path="/academy/plan-trade/welcome" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeWelcomePage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/plan-trade/expired" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeExpiredPage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/plan-trade/onboarding" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeOnboardingPage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/plan-trade/asset/:symbol" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeAssetDetailPage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/pattern-picker-demo" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PatternPickerDemoPage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/plan-trade" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeGatePage /></FeatureGate></LazyRoute>} />
                <Route path="/academy/plan-trade/*" element={<LazyRoute><FeatureGate featureKey="academy_plan_trade"><PlanTradeGatePage /></FeatureGate></LazyRoute>} />

                {/* Fallback für noch nicht implementierte Modul-Routen */}
                <Route
                  path="/academy/*"
                  element={
                    <div className="flex items-center justify-center min-h-[50vh]">
                      <div className="text-center">
                        <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Modul folgt</h1>
                        <p className="mb-4 text-gray-500 dark:text-gray-400">Die Modul-Seiten sind noch in Arbeit.</p>
                        <a href="/academy" className="text-brand hover:text-brand-hover transition-colors">Zurück zur Modul-Übersicht</a>
                      </div>
                    </div>
                  }
                />
              </Route>

              {/* ============================================== */}
              {/* Admin Routes mit persistenter Sidebar          */}
              {/* Alle Admin-Seiten sind lazy loaded             */}
              {/* ============================================== */}
              <Route element={<ProtectedRoute requiredRole="admin"><AuthenticatedLayout /></ProtectedRoute>}>
                <Route path="/admin" element={<LazyRoute><AdminDashboard /></LazyRoute>} />
                <Route path="/admin-dashboard/users/:userId" element={<LazyRoute><AdminUserDetailPage /></LazyRoute>} />
                <Route path="/admin-dashboard/:tab?" element={<LazyRoute><AdminDashboard /></LazyRoute>} />
                <Route path="/admin-dashboard/feedback" element={<LazyRoute><AdminFeedbackManagement /></LazyRoute>} />
                <Route path="/admin-dashboard/beta" element={<LazyRoute><BetaManagementPage /></LazyRoute>} />
                <Route path="/admin-dashboard/system/:tab?" element={<LazyRoute><SystemPageTW /></LazyRoute>} />
                <Route path="/admin-dashboard/appointments/:tab?" element={<LazyRoute><AdminAppointmentsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/fcms/dashboard/:tab?" element={<LazyRoute><AdminFcmsDashboardPage /></LazyRoute>} />
                <Route path="/admin-dashboard/fcms/youtube-matching/:tab?" element={<LazyRoute><AdminYoutubeMatchingPage /></LazyRoute>} />
                <Route path="/admin-dashboard/availability" element={<Navigate to="/admin-dashboard/appointments/verfuegbarkeit" replace />} />
                <Route path="/admin-dashboard/email-preview" element={<LazyRoute><EmailPreviewPage /></LazyRoute>} />
                <Route path="/admin-dashboard/email-queue" element={<LazyRoute><EmailQueuePage /></LazyRoute>} />
                <Route path="/admin-dashboard/revenue" element={<LazyRoute><AdminRevenuePage /></LazyRoute>} />
                {/* Konsolidierung 2026-05-09 abgeschlossen: Rechnungs-Verwaltung
                    und Rechnungseinstellungen leben nun unter /accounting/*. */}
                <Route path="/admin-dashboard/invoices" element={<Navigate to="/accounting/invoices" replace />} />
                <Route path="/admin-dashboard/revocations" element={<LazyRoute><AdminRevocationsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/settings/invoices" element={<Navigate to="/accounting/company-info" replace />} />
                <Route path="/admin-dashboard/settings/pricing" element={<LazyRoute><PricingOverviewPage /></LazyRoute>} />
                <Route path="/admin-dashboard/settings/pricing/:categorySlug" element={<LazyRoute><PricingModulesPage /></LazyRoute>} />
                <Route path="/admin-dashboard/settings/pricing/:categorySlug/:moduleId" element={<LazyRoute><PricingModuleEditPage /></LazyRoute>} />
                <Route path="/admin-dashboard/settings/coupons" element={<LazyRoute><CouponManagementPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog" element={<LazyRoute><AdminBlogPostsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/posts" element={<LazyRoute><AdminBlogPostsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/new" element={<LazyRoute><AdminBlogPostNewPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/posts/:id/edit" element={<LazyRoute><AdminBlogPostEditPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/posts/:id/preview" element={<LazyRoute><AdminBlogPostPreviewPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/media" element={<LazyRoute><AdminMediaLibraryPageTW /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/rss-feeds" element={<LazyRoute><AdminRssFeedsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/blog/premium-support/:tab?" element={<LazyRoute><AdminBlogPremiumSupportPage /></LazyRoute>} />
                <Route path="/admin-dashboard/asset-management" element={<LazyRoute><AssetManagementPageTW /></LazyRoute>} />
                <Route path="/admin-dashboard/announcements" element={<LazyRoute><AdminAnnouncementsPage /></LazyRoute>} />
                <Route path="/admin-dashboard/affiliates" element={<LazyRoute><AdminAffiliatesPage /></LazyRoute>} />
                <Route path="/admin-dashboard/styleguide" element={<LazyRoute><AdminStyleguidePage /></LazyRoute>} />
              </Route>

              {/* Help & Legal Pages */}
              <Route
                path="/help"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-950">
                    <div className="text-center">
                      <h1 className="text-3xl font-bold mb-4 text-white">Hilfe & Support</h1>
                      <p className="mb-4 text-gray-400">Coming soon</p>
                      <a href="/" className="text-brand hover:text-brand-hover">Zurück zur Startseite</a>
                    </div>
                  </div>
                }
              />

              {/* Contact Page */}
              <Route path="/kontakt" element={<Suspense fallback={<PageLoader />}><ContactPageTW /></Suspense>} />

              {/* Legal Pages - lazy loaded */}
              <Route path="/impressum" element={<Suspense fallback={<PageLoader />}><LegalPage /></Suspense>} />
              <Route path="/datenschutz" element={<Suspense fallback={<PageLoader />}><LegalPage /></Suspense>} />
              <Route path="/agb" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />

              {/* Kuendigungsbutton § 312k BGB */}
              <Route path="/kuendigen" element={<Suspense fallback={<PageLoader />}><CancellationPage /></Suspense>} />
              <Route path="/kuendigen/bestaetigen/:token" element={<Suspense fallback={<PageLoader />}><CancellationConfirmPage /></Suspense>} />

              {/* Widerrufsbutton § 356a BGB (ab 19.06.2026 Pflicht) */}
              <Route path="/widerrufen" element={<Suspense fallback={<PageLoader />}><WithdrawalPage /></Suspense>} />
              <Route path="/widerrufen/bestaetigen/:token" element={<Suspense fallback={<PageLoader />}><WithdrawalConfirmPage /></Suspense>} />

              {/* Legacy routes for compatibility */}
              <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><LegalPage /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />

              {/* 404 Route */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-950">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold mb-4 text-brand">404</h1>
                      <h2 className="text-2xl font-semibold mb-4 text-white">Seite nicht gefunden</h2>
                      <p className="mb-6 text-gray-400">Diese Seite existiert nicht oder wurde verschoben.</p>
                      <a
                        href="/"
                        className="inline-block px-6 py-3 rounded-md transition-colors font-medium bg-brand text-gray-950 hover:bg-brand-hover"
                      >
                        Zur Startseite
                      </a>
                    </div>
                  </div>
                }
              />
            </Routes>
            
            {/* Cookie Banner */}
            <CookieBannerTW />

            {/* Logo Icon (Desktop only, nur auf Landing Page) */}
            <TWLogoIcon />

            {/* Theme Toggle ist jetzt in der Sidebar */}

            {/* Login Icon (Desktop only, nicht-angemeldete User, nicht auf Login/Register) */}
            <TWLoginIcon />

            {/* Content Width Toggle ist jetzt in der Sidebar */}

            {/* Feedback-Button entfernt -- Zugang über Sidebar-Navigation */}

            {/* Toast Notifications werden vom ToastProvider gerendert */}
            </div>
          </NotificationProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;